// apps/provider/src/services/api/pharmacy.js
// Pharmacy search and lookup methods
//
// Methods: getPharmacies, searchPharmacies, getPrescriberRecentPharmacies, getPharmacyByNcpdpId
//
// Response types: @prism-ehr/shared → Pharmacy

/**
 * @returns {Object} Pharmacy domain methods (bound to `this` of the composed apiService)
 */
export function createPharmacyMethods() {
  return {
    /**
     * Get all pharmacies.
     *
     * Xano: GET /pharmacy
     * Xano file: 467_pharmacy_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').Pharmacy[]>}
     */
    getPharmacies() {
      return this.request('/pharmacy', {
        method: 'GET'
      });
    },

    /**
     * Search pharmacies by term, zip, or exact match.
     *
     * Xano: GET /search_pharmacies
     * Xano file: 544_search_pharmacies_GET.xs
     *
     * @param {Object} params
     * @param {string} [params.search_term]
     * @param {string} [params.patient_zip]
     * @param {boolean} [params.exact_match]
     * @returns {Promise<import('@prism-ehr/shared').Pharmacy[]>}
     */
    searchPharmacies(params) {
      const queryParams = new URLSearchParams();

      if (params.search_term) {
        queryParams.append('search_term', params.search_term);
      }

      if (params.patient_zip) {
        queryParams.append('patient_zip', params.patient_zip);
      }

      if (params.exact_match !== undefined) {
        queryParams.append('exact_match', params.exact_match ? 'true' : 'false');
      }

      const endpoint = `/search_pharmacies?${queryParams.toString()}`;
      return this.request(endpoint, {
        method: 'GET'
      });
    },

    /**
     * Get prescriber's recently used pharmacies.
     *
     * Xano: GET /prescriber_recent_pharmacies
     * Xano file: 545_prescriber_recent_pharmacies_GET.xs
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').Pharmacy[]>}
     */
    getPrescriberRecentPharmacies(userId) {
      return this.request(`/prescriber_recent_pharmacies?user_id=${userId}`, {
        method: 'GET'
      });
    },

    /**
     * Get specific pharmacy by NCPDP ID.
     *
     * Xano: GET /preferred_pharmacy
     * Xano file: 547_preferred_pharmacy_GET.xs
     *
     * @param {string} ncpdpId
     * @returns {Promise<import('@prism-ehr/shared').Pharmacy>}
     */
    getPharmacyByNcpdpId(ncpdpId) {
      return this.request(`/preferred_pharmacy?ncpdp_id=${ncpdpId}`, {
        method: 'GET'
      });
    },
  };
}
